import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "./api.ts";
import { stringify } from "./frontmatter.ts";
import type { IndexItem, FolderMeta } from "./types.ts";

const CAT_ORDER = ["Journey", "Homelab", "Selfhost", "Blog", "Access"];

/** ASCII-only slug for the filename (titles are often Chinese; filenames aren't). */
function slugify(s: string): string {
  return s.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
}

type DocEff = IndexItem & { eff: number };
type SubFolder = { dir: string; label: string; pos: number; docs: DocEff[] };
type RootNode = { type: "doc"; doc: DocEff; pos: number } | { type: "folder"; folder: SubFolder; pos: number };
type Placement = { dir: string; level: number; prev: number | null; next: number | null };

function withEff(docs: IndexItem[]): DocEff[] {
  const sorted = docs
    .slice()
    .sort((a, b) => (a.order ?? 9999) - (b.order ?? 9999) || a.path.localeCompare(b.path, undefined, { numeric: true }));
  let prev = 0;
  return sorted.map((it) => {
    let eff = typeof it.order === "number" ? it.order : prev + 1;
    if (eff <= prev) eff = prev + 1;
    prev = eff;
    return { ...it, eff };
  });
}

export function NewArticle(props: {
  categories: string[];
  items: IndexItem[];
  folders: FolderMeta[];
  canEdit: boolean;
  onClose: () => void;
  onCreated: (path: string) => void;
}) {
  const cats = useMemo(
    () =>
      [...props.categories].sort((a, b) => {
        const ia = CAT_ORDER.indexOf(a), ib = CAT_ORDER.indexOf(b);
        return (ia < 0 ? 9 : ia) - (ib < 0 ? 9 : ib) || a.localeCompare(b);
      }),
    [props.categories]
  );

  const [cat, setCat] = useState<string>(cats.includes("Blog") ? "Blog" : cats[0] ?? "Blog");
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [placementIndex, setPlacementIndex] = useState(0);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null); // 訪客動作被攔下的提示，非錯誤

  // drag state
  const treeRef = useRef<HTMLDivElement>(null);
  const [dragging, setDragging] = useState(false);
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);
  const [ghost, setGhost] = useState<{ x: number; y: number } | null>(null);

  const isBlog = cat === "Blog";

  // Build the category as Docusaurus renders it: root docs + subfolders interleaved
  // on one position axis (folders carry their _category_.json position).
  const tree = useMemo(() => {
    if (isBlog) return { base: "", rootSeq: [] as RootNode[], placements: [] as Placement[] };
    let base = `docs/${cat.toLowerCase()}`;
    const byDir = new Map<string, IndexItem[]>();
    for (const it of props.items) {
      if (it.category !== cat || !it.path.startsWith("docs/")) continue;
      base = it.path.split("/").slice(0, 2).join("/");
      const dir = it.path.slice(0, it.path.lastIndexOf("/"));
      (byDir.get(dir) ?? byDir.set(dir, []).get(dir)!).push(it);
    }
    const rootDocs = withEff(byDir.get(base) ?? []);
    const metas = props.folders.filter((f) => f.category === cat);
    const subDirs = new Set<string>();
    for (const d of byDir.keys()) if (d !== base) subDirs.add(d);
    for (const f of metas) subDirs.add(f.dir);
    const subFolders: SubFolder[] = [...subDirs].map((dir) => {
      const meta = metas.find((f) => f.dir === dir);
      return {
        dir,
        label: meta?.label || dir.slice(base.length + 1),
        pos: meta && meta.position != null ? meta.position : 9999,
        docs: withEff(byDir.get(dir) ?? []),
      };
    });
    const rootSeq: RootNode[] = [
      ...rootDocs.map((d) => ({ type: "doc", doc: d, pos: d.eff } as RootNode)),
      ...subFolders.map((f) => ({ type: "folder", folder: f, pos: f.pos } as RootNode)),
    ].sort((a, b) => a.pos - b.pos);
    const placements: Placement[] = [];
    let prevRoot: number | null = null;
    for (let i = 0; i <= rootSeq.length; i++) {
      placements.push({ dir: base, level: 0, prev: prevRoot, next: i < rootSeq.length ? rootSeq[i].pos : null });
      if (i === rootSeq.length) break;
      const node = rootSeq[i];
      if (node.type === "doc") prevRoot = node.doc.eff;
      else {
        const fdocs = node.folder.docs;
        let prevF: number | null = null;
        for (let j = 0; j <= fdocs.length; j++) {
          placements.push({ dir: node.folder.dir, level: 1, prev: prevF, next: j < fdocs.length ? fdocs[j].eff : null });
          if (j === fdocs.length) break;
          prevF = fdocs[j].eff;
        }
        prevRoot = node.folder.pos;
      }
    }
    return { base, rootSeq, placements };
  }, [isBlog, props.items, props.folders, cat]);

  useEffect(() => setPlacementIndex(Math.max(0, tree.placements.length - 1)), [cat, tree.placements.length]);

  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 4000);
    return () => clearTimeout(t);
  }, [notice]);

  const pl = tree.placements[placementIndex] ?? tree.placements[0];
  const chosenDir = pl?.dir ?? tree.base ?? `docs/${cat.toLowerCase()}`;

  const position = useMemo(() => {
    if (!pl) return 1;
    const { prev, next } = pl;
    if (prev == null) return next == null ? 1 : next > 1 ? next - 1 : Number((next / 2).toFixed(3));
    if (next == null) return prev + 1;
    return Number(((prev + next) / 2).toFixed(3));
  }, [pl]);

  const effectiveSlug = slugEdited ? slug : slugify(title);
  const filename = effectiveSlug || "untitled";
  const path = isBlog
    ? `blog/${date.slice(0, 4)}/${date}-${filename}.md`
    : `${chosenDir}/${filename}.md`;

  const canCreate = title.trim() !== "" && effectiveSlug !== "" && !busy;

  function toggleFolder(dir: string) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(dir)) next.delete(dir);
      else next.add(dir);
      return next;
    });
  }

  // ---- drag: find the nearest gap to a pointer Y, live-highlight, drop on release ----
  function nearestGap(y: number): number {
    const gaps = treeRef.current?.querySelectorAll<HTMLElement>(".nf-gap") ?? [];
    let best = placementIndex, bestDist = Infinity;
    gaps.forEach((g) => {
      const r = g.getBoundingClientRect();
      const d = Math.abs(r.top + r.height / 2 - y);
      if (d < bestDist) { bestDist = d; best = Number(g.dataset.pidx); }
    });
    return best;
  }
  function onHandleDown(e: React.PointerEvent) {
    e.preventDefault();
    setDragging(true);
    setGhost({ x: e.clientX, y: e.clientY });
    setHoverIdx(placementIndex);
    (e.target as Element).setPointerCapture?.(e.pointerId);
  }
  function onHandleMove(e: React.PointerEvent) {
    if (!dragging) return;
    setGhost({ x: e.clientX, y: e.clientY });
    setHoverIdx(nearestGap(e.clientY));
  }
  function onHandleUp() {
    if (!dragging) return;
    if (hoverIdx != null) setPlacementIndex(hoverIdx);
    setDragging(false);
    setGhost(null);
    setHoverIdx(null);
  }

  async function create() {
    if (!props.canEdit) {
      setNotice("訪客唯讀模式——這個動作不會送出，登入後即可建立文章。");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const content = isBlog
        ? stringify(
            { slug: filename, title, authors: ["w"], tags: [], draft: true },
            "\n<!-- truncate -->\n\n在這裡開始寫…\n"
          )
        : stringify(
            { sidebar_label: title, sidebar_position: position, draft: true },
            `# ${title}\n\n在這裡開始寫…\n`
          );
      await api.save({ path, content, message: `content: create ${path}` });
      props.onCreated(path);
    } catch (e) {
      setError(String((e as Error).message));
    } finally {
      setBusy(false);
    }
  }

  function newItemRow(level: number) {
    return (
      <div className={`nf-oitem new ${level === 1 ? "sub" : ""} ${dragging ? "dragsrc" : ""}`} key="__new">
        <span className="nf-otitle">{title.trim() || "（你的新文章）"}</span>
        <span
          className="nf-drag"
          onPointerDown={onHandleDown}
          onPointerMove={onHandleMove}
          onPointerUp={onHandleUp}
          onPointerCancel={onHandleUp}
          aria-label="拖曳排序"
          title="拖曳到想要的位置"
        >
          ⠿
        </span>
      </div>
    );
  }

  // Build display rows; gaps are drop zones carrying their placement index.
  const treeRows = (() => {
    const rows: JSX.Element[] = [];
    let p = 0;
    const gap = () => {
      const idx = p;
      rows.push(
        <div key={"g" + idx} className={`nf-gap ${dragging && hoverIdx === idx ? "active" : ""}`} data-pidx={idx} />
      );
      if (idx === placementIndex) rows.push(newItemRow(tree.placements[idx]?.level ?? 0));
      p++;
    };
    for (let i = 0; i <= tree.rootSeq.length; i++) {
      gap();
      if (i === tree.rootSeq.length) break;
      const node = tree.rootSeq[i];
      if (node.type === "doc") {
        rows.push(
          <div className="nf-oitem" key={node.doc.path}>
            <span className="nf-otitle">{node.doc.title}</span>
          </div>
        );
      } else {
        const f = node.folder;
        const isFold = collapsed.has(f.dir);
        rows.push(
          <button className="nf-folder" key={"dir:" + f.dir} type="button" onClick={() => toggleFolder(f.dir)}>
            <span className="nf-otitle">{f.label}</span>
            <span className={`nf-fold-chev ${isFold ? "closed" : ""}`} aria-hidden>▾</span>
          </button>
        );
        const fdocs = f.docs;
        if (isFold) {
          const start = p, end = p + fdocs.length;
          if (placementIndex >= start && placementIndex <= end) rows.push(newItemRow(1));
          p = end + 1;
        } else {
          for (let j = 0; j <= fdocs.length; j++) {
            gap();
            if (j === fdocs.length) break;
            rows.push(
              <div className="nf-oitem sub" key={fdocs[j].path}>
                <span className="nf-otitle">{fdocs[j].title}</span>
              </div>
            );
          }
        }
      }
    }
    return rows;
  })();

  return (
    <div className="editor">
      <header className="topbar">
        <button className="btn ghost" onClick={props.onClose}>‹ 返回</button>
        <div className="grow"><div className="path">新文章</div></div>
        <button className="btn primary" onClick={create} disabled={!canCreate}>
          {busy ? "建立中…" : "建立草稿"}
        </button>
      </header>

      {error && <div className="banner error">{error}</div>}
      {notice && <div className="banner notice">{notice}</div>}

      <div className="newform">
        <div className="nf-field">
          <span className="nf-label">要放在哪個分類？</span>
          <div className="nf-cats">
            {cats.map((c) => (
              <button key={c} type="button" className={`chip ${cat === c ? "on" : ""}`} onClick={() => setCat(c)}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <label className="nf-field">
          <span className="nf-label">標題</span>
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="想到什麼就寫，中文也可以"
            autoFocus
          />
        </label>

        <label className="nf-field">
          <span className="nf-label">
            檔名 <em>英文小寫，例如 my-new-post</em>
          </span>
          <input
            value={effectiveSlug}
            onChange={(e) => { setSlug(e.target.value); setSlugEdited(true); }}
            placeholder="my-new-post"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </label>

        {isBlog ? (
          <label className="nf-field">
            <span className="nf-label">日期</span>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </label>
        ) : (
          <div className="nf-field">
            <span className="nf-label">
              放在哪裡？<em>拖曳 ⠿ 把文章移到資料夾與位置；點資料夾可收合</em>
            </span>
            <div ref={treeRef} className={`nf-tree ${dragging ? "dragging" : ""}`}>
              {treeRows}
            </div>
          </div>
        )}

        <div className="nf-field">
          <span className="nf-label">會建立這個檔案</span>
          <code className="pathpreview">{path}</code>
        </div>

        <p className="nf-note">
          建立後是<b>草稿</b>，只有你看得到，不會出現在網站上——可以慢慢寫，寫好再按「發表」。
        </p>
      </div>

      {ghost && (
        <div className="nf-ghost" style={{ left: ghost.x, top: ghost.y }}>
          {title.trim() || "（你的新文章）"}
        </div>
      )}
    </div>
  );
}
